
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Influencer } from "@/types/influencer";
import { Calendar, Heart, Star, Users, Clock } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";

interface ProfileHeaderProps {
  influencer: Influencer;
}

export const ProfileHeader = ({ influencer }: ProfileHeaderProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [liked, setLiked] = useState(false);

  const handleLike = () => {
    setLiked(!liked);
    toast({
      title: liked ? "Removed from favorites" : "Added to favorites",
      description: liked ? "Influencer removed from your favorites" : "Influencer added to your favorites",
    });
  };

  // Format price with commas
  const formatPrice = (price: number) => {
    return price.toLocaleString();
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-background to-primary/10 p-8 shadow-lg border border-primary/10">
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -mr-48 -mt-48 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
      
      <div className="flex flex-col lg:flex-row gap-8 relative z-10">
        {/* Profile Image */}
        <div className="w-full lg:w-80 flex-shrink-0">
          <div className="rounded-2xl overflow-hidden relative aspect-square shadow-xl border-2 border-primary/20 group">
            <img 
              src={influencer.image || "/placeholder.svg"} 
              alt={influencer.name} 
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            {influencer.featured && (
              <Badge className="absolute top-4 right-4 bg-primary shadow-lg">
                {t("featured")}
              </Badge>
            )}
          </div>
        </div>

        {/* Profile Info */}
        <div className="flex-1 flex flex-col justify-between">
          <div>
            {/* Name & Actions */}
            <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  {influencer.name}
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                  {influencer.rating > 0 && (
                    <Badge variant="outline" className="flex items-center gap-1 bg-primary/10 border-primary/20">
                      <Star className="h-3.5 w-3.5 text-primary fill-primary" />
                      <span className="font-semibold">{influencer.rating}</span>
                    </Badge>
                  )}
                  <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                    {influencer.category}
                  </Badge>
                </div>
              </div>
              
              <Button 
                size="icon" 
                variant="outline" 
                className={`rounded-full h-12 w-12 transition-all ${liked ? 'bg-primary/10 text-primary border-primary/30' : 'border-primary/20'}`}
                onClick={handleLike}
              >
                <Heart className={`h-5 w-5 ${liked ? 'fill-primary text-primary' : ''}`} />
              </Button>
            </div>

            {/* Description */}
            {influencer.description && (
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                {influencer.description}
              </p>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Users className="h-4 w-4 text-primary" />
                  <span>Followers</span>
                </div>
                <div className="text-xl font-bold text-foreground">{influencer.followers}</div>
              </div>
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Availability</span>
                </div>
                <div className="text-xl font-bold text-foreground">{influencer.availability}</div>
              </div>
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 col-span-2 sm:col-span-1">
                <div className="text-muted-foreground text-sm mb-1">Starting from</div>
                <div className="text-xl font-bold text-primary">{formatPrice(influencer.price)} SAR</div>
              </div>
            </div>

            {/* Expertise Tags */}
            {influencer.expertise && influencer.expertise.length > 0 && (
              <div className="mb-6">
                <div className="text-sm text-muted-foreground mb-2">{t("expertise")}</div>
                <div className="flex flex-wrap gap-2">
                  {influencer.expertise.map((skill, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary" 
                      className="py-1.5 px-3 bg-primary/10 border-primary/10 text-foreground capitalize"
                    >
                      {skill.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Book Button */}
          <Button 
            size="lg" 
            className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300"
            asChild
          >
            <Link to={`/influencers/${influencer.id}/book`}>
              <Calendar className="mr-2 h-5 w-5" />
              {t("bookInfluencer")}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};
